import { useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { isSupabaseConfigured } from "@/lib/supabase";
import type { UserRole } from "@/types/auth";

interface ProtectedRouteProps {
  component: React.ComponentType;
  // Roles allowed on this route. Defaults to both admin tiers.
  allowedRoles?: UserRole[];
  // Where to send authenticated users who don't meet allowedRoles.
  // Defaults to "/" (handbook). Used to bounce acrrm_admin from /admin to /admin/reports.
  fallbackPath?: string;
}

const DEFAULT_ALLOWED: UserRole[] = ['internal_admin', 'acrrm_admin'];

export function ProtectedRoute({
  component: Component,
  allowedRoles = DEFAULT_ALLOWED,
  fallbackPath = "/",
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasAnyRole } = useAuth();
  const [, setLocation] = useLocation();

  const isAllowed = hasAnyRole(allowedRoles);

  useEffect(() => {
    if (isLoading) return;
    if (!isSupabaseConfigured()) return;

    if (!isAuthenticated) {
      setLocation("/login");
    } else if (!isAllowed) {
      setLocation(fallbackPath);
    }
  }, [isLoading, isAuthenticated, isAllowed, fallbackPath, setLocation]);

  if (!isSupabaseConfigured()) {
    return <Component />;
  }

  if (isLoading) {
    return <div className="flex h-screen items-center justify-center">Loading...</div>;
  }

  if (!isAuthenticated || !isAllowed) {
    return null;
  }

  return <Component />;
}
